/* eslint-disable max-lines */
import { BulkSelector } from '@core/components/BulkSelector';
import type { BulkSelectableItem } from '@core/components/BulkSelector';
import { Button, Slider } from '@soteria/design-system-ve';
import { useState, useRef, useCallback, useEffect } from 'react';

import * as styles from './LabelWithSeveritySelector.css';
import * as sliderInputStyles from './SliderInputStyles.css';

// Utility function to convert severity number to human-readable label
const getSeverityLabel = (severity: number, maxSeverity: number = 3): string => {
  if (maxSeverity <= 3) {
    // For 3-level system: 1=Low, 2=Medium, 3=High
    switch (severity) {
      case 1:
        return 'Low';
      case 2:
        return 'Medium';
      case 3:
        return 'High';
      default:
        return severity.toString();
    }
  } else {
    // For larger scales, just use numbers
    return severity.toString();
  }
};

// Helper function to validate numeric input and clamp to valid range
const validateAndClampSeverity = (value: string, min: number, max: number): number => {
  const numValue = parseInt(value, 10);

  if (isNaN(numValue)) return min;

  return Math.max(min, Math.min(max, numValue));
};

// Inline Editable Value Component
interface InlineEditableValueProps {
  value: number;
  min: number;
  max: number;
  compact?: boolean;
  onChange: (value: number) => void;
  ariaLabel?: string;
}

function InlineEditableValue({ value, min, max, compact = false, onChange, ariaLabel }: InlineEditableValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    setIsEditing(true);
    setEditValue(value.toString());
  }, [value]);

  const handleBlur = useCallback(() => {
    const numValue = validateAndClampSeverity(editValue, min, max);

    onChange(numValue);
    setIsEditing(false);
  }, [editValue, min, max, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleBlur();
      } else if (e.key === 'Escape') {
        setEditValue(value.toString());
        setIsEditing(false);
      }
    },
    [handleBlur, value]
  );

  // Auto-focus when switching to edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        aria-label={ariaLabel}
        className={compact ? sliderInputStyles.compactInlineEditInput : sliderInputStyles.inlineEditInput}
        max={max}
        min={min}
        type="number"
        value={editValue}
        onBlur={handleBlur}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <button
      aria-label={ariaLabel || `Click to edit value ${value}`}
      className={compact ? sliderInputStyles.compactInlineEditableValue : sliderInputStyles.inlineEditableValue}
      type="button"
      onClick={handleClick}
    >
      {value}
    </button>
  );
}

export interface LabelWithSeverity {
  labelId: string;
  severity: number;
}

export interface LabelOption extends BulkSelectableItem {
  id: string;
  labelName: string;
  maxSeverity?: number;
}

interface LabelWithSeveritySelectorProps {
  availableLabels: LabelOption[];
  selectedLabels: LabelWithSeverity[];
  loading?: boolean;
  title?: string;
  emptyStateMessage?: string;
  severityMin?: number;
  defaultSeverity?: number;
  defaultMaxSeverity?: number;
  showSeverityDescription?: boolean;
  enableBulkSelection?: boolean;
  itemsPerPage?: number;
  onChange: (labels: LabelWithSeverity[]) => void;
}

// eslint-disable-next-line complexity
export function LabelWithSeveritySelector({
  availableLabels,
  selectedLabels,
  loading,
  title = 'Labels',
  emptyStateMessage = 'No labels selected. Add labels to define criteria.',
  severityMin = 1,
  defaultSeverity = 2,
  defaultMaxSeverity = 3,
  showSeverityDescription = true,
  enableBulkSelection = false,
  itemsPerPage = 8,
  onChange,
}: LabelWithSeveritySelectorProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [severity, setSeverity] = useState(defaultSeverity);

  // Bulk selection state
  const [bulkSelectedLabelIds, setBulkSelectedLabelIds] = useState<string[]>([]);
  const [bulkSeverity, setBulkSeverity] = useState(defaultSeverity);

  // Get the max severity for the currently selected label
  const getMaxSeverityForLabel = (labelId: string): number => {
    const label = availableLabels.find(l => l.id === labelId);

    return label?.maxSeverity ?? defaultMaxSeverity;
  };

  // Get the max severity for display purposes
  const currentMaxSeverity = selectedLabelId ? getMaxSeverityForLabel(selectedLabelId) : defaultMaxSeverity;

  const unselectedLabels = availableLabels.filter(label => !selectedLabels.some(selected => selected.labelId === label.id));

  const handleAddLabel = () => {
    const maxSev = getMaxSeverityForLabel(selectedLabelId);

    if (selectedLabelId && selectedLabelId.trim() && severity >= severityMin && severity <= maxSev) {
      const newLabel: LabelWithSeverity = {
        labelId: selectedLabelId.trim(),
        severity: Math.min(severity, maxSev),
      };

      onChange([...selectedLabels, newLabel]);
      setSelectedLabelId('');
      setSeverity(defaultSeverity);
      setShowAddForm(false);
    }
  };

  // Bulk selection handlers

  const handleBulkAddLabels = () => {
    if (bulkSelectedLabelIds.length === 0) return;

    const newLabels: LabelWithSeverity[] = bulkSelectedLabelIds.map(labelId => {
      const maxSev = getMaxSeverityForLabel(labelId);

      return {
        labelId,
        severity: Math.min(bulkSeverity, maxSev),
      };
    });

    onChange([...selectedLabels, ...newLabels]);
    setBulkSelectedLabelIds([]);
    setBulkSeverity(defaultSeverity);
    setShowAddForm(false);
  };

  const handleRemoveLabel = (labelId: string) => {
    onChange(selectedLabels.filter(label => label.labelId !== labelId));
  };

  const handleSeverityChange = (labelId: string, newSeverity: number) => {
    onChange(selectedLabels.map(label => (label.labelId === labelId ? { ...label, severity: newSeverity } : label)));
  };

  const getLabelName = (labelId: string) => availableLabels.find(label => label.id === labelId)?.labelName || labelId;

  const getSeverityColor = (): 'success' =>
    // Always return green ('success') color for consistent visual styling
    'success';

  if (loading) {
    return <div className={styles.loading}>Loading labels...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        {unselectedLabels.length > 0 && <Button onClick={() => setShowAddForm(!showAddForm)}>{showAddForm ? 'Cancel' : 'Add Label(s)'}</Button>}
      </div>

      {/* Add Label Form */}
      {showAddForm && (
        <div className={styles.addForm}>
          {enableBulkSelection ? (
            // Bulk Selection Mode
            <div className={styles.bulkSelectionContainer}>
              <BulkSelector
                className={styles.formField}
                emptyPageMessage="No labels available on this page"
                emptySearchMessage="No labels found matching your search"
                getItemSearchText={item => item.labelName}
                items={unselectedLabels}
                itemsPerPage={itemsPerPage}
                legend="Select Labels"
                renderItem={item => item.labelName}
                searchAriaLabel="Search labels"
                searchPlaceholder="Search labels..."
                selectedItemIds={bulkSelectedLabelIds}
                onSelectedItemsChange={setBulkSelectedLabelIds}
              />

              {bulkSelectedLabelIds.length > 0 && (
                <div className={styles.bulkSeveritySection}>
                  <div className={styles.formField}>
                    <div className={sliderInputStyles.sliderInputContainer}>
                      <div className={sliderInputStyles.verticalSliderContainer}>
                        <div className={sliderInputStyles.sliderLabelHeader}>
                          <span className={sliderInputStyles.sliderLabelText}>
                            Default Severity for Selected Labels: {getSeverityLabel(bulkSeverity, defaultMaxSeverity)} ({bulkSeverity}/{defaultMaxSeverity})
                          </span>
                          <InlineEditableValue
                            ariaLabel="Severity value input"
                            max={defaultMaxSeverity}
                            min={severityMin}
                            value={bulkSeverity}
                            onChange={setBulkSeverity}
                          />
                        </div>
                        <div className={sliderInputStyles.sliderWrapper}>
                          <Slider
                            showMinMaxLabels
                            aria-describedby={showSeverityDescription ? 'bulk-severity-description' : undefined}
                            color={getSeverityColor()}
                            id="bulkSeveritySlider"
                            label=""
                            max={defaultMaxSeverity}
                            min={severityMin}
                            step={1}
                            value={bulkSeverity}
                            onChange={setBulkSeverity}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.formActions}>
                <Button disabled={bulkSelectedLabelIds.length === 0} onClick={handleBulkAddLabels}>
                  Add Selected Labels ({bulkSelectedLabelIds.length})
                </Button>
                <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            // Single Selection Mode (existing functionality)
            <>
              <div className={styles.formField}>
                <label className={styles.fieldLabel} htmlFor="labelSelect">
                  Select Label
                </label>
                <select className={styles.select} id="labelSelect" value={selectedLabelId} onChange={e => setSelectedLabelId(e.target.value)}>
                  <option value="">Choose a label...</option>
                  {unselectedLabels.map(label => (
                    <option key={label.id} value={label.id}>
                      {label.labelName}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formField}>
                <div className={sliderInputStyles.sliderInputContainer}>
                  <div className={sliderInputStyles.verticalSliderContainer}>
                    <div className={sliderInputStyles.sliderLabelHeader}>
                      <span className={sliderInputStyles.sliderLabelText}>
                        Severity Threshold: {getSeverityLabel(severity, currentMaxSeverity)} ({severity}/{currentMaxSeverity})
                      </span>
                      <InlineEditableValue
                        ariaLabel="Severity value input"
                        max={currentMaxSeverity}
                        min={severityMin}
                        value={severity}
                        onChange={setSeverity}
                      />
                    </div>
                    <div className={sliderInputStyles.sliderWrapper}>
                      <Slider
                        showMinMaxLabels
                        aria-describedby={showSeverityDescription ? 'severity-description' : undefined}
                        color={getSeverityColor()}
                        id="severitySlider"
                        label=""
                        max={currentMaxSeverity}
                        min={severityMin}
                        step={1}
                        value={Math.min(severity, currentMaxSeverity)}
                        onChange={setSeverity}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.formActions}>
                <Button disabled={!selectedLabelId || !selectedLabelId.trim()} onClick={handleAddLabel}>
                  Add Label
                </Button>
                <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Selected Labels List */}
      {selectedLabels.length > 0 && (
        <div>
          <div className={styles.sectionTitle}>Selected Labels ({selectedLabels.length})</div>
          <div className={styles.labelsList}>
            {selectedLabels.map(label => {
              const labelMaxSeverity = getMaxSeverityForLabel(label.labelId);
              const displaySeverity = Math.min(label.severity, labelMaxSeverity);

              return (
                <div key={label.labelId} className={styles.labelItem}>
                  <div className={styles.labelInfo}>
                    <div className={styles.labelName}>{getLabelName(label.labelId)}</div>
                    <div className={styles.labelSeverity}>
                      Severity threshold: {getSeverityLabel(displaySeverity, labelMaxSeverity)} ({displaySeverity}/{labelMaxSeverity})
                    </div>
                  </div>
                  <div className={styles.labelControls}>
                    <div className={sliderInputStyles.compactSliderInputRow}>
                      <div className={sliderInputStyles.compactSliderWrapper}>
                        <Slider
                          aria-describedby={showSeverityDescription ? `severity-desc-${label.labelId}` : undefined}
                          aria-label={`Severity threshold for ${getLabelName(label.labelId)}: ${getSeverityLabel(displaySeverity, labelMaxSeverity)} (${displaySeverity} out of ${labelMaxSeverity})`}
                          color={getSeverityColor()}
                          max={labelMaxSeverity}
                          min={severityMin}
                          showMinMaxLabels={false}
                          size="small"
                          step={1}
                          value={displaySeverity}
                          onChange={newValue => handleSeverityChange(label.labelId, newValue)}
                        />
                      </div>
                      <InlineEditableValue
                        compact
                        ariaLabel={`Severity value input for ${getLabelName(label.labelId)}`}
                        max={labelMaxSeverity}
                        min={severityMin}
                        value={displaySeverity}
                        onChange={newValue => handleSeverityChange(label.labelId, newValue)}
                      />
                    </div>
                    <Button aria-label={`Remove ${getLabelName(label.labelId)} label`} onClick={() => handleRemoveLabel(label.labelId)}>
                      Remove
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedLabels.length === 0 && (
        <div aria-live="polite" className={styles.emptyState} role="status">
          {emptyStateMessage}
        </div>
      )}
    </div>
  );
}
